import { Alert, Badge, Button, Card, Spinner, useToast } from '@cartly/ui';
import { RefreshCw } from 'lucide-react';
import { api, errorMessage } from '../../api/client';
import { StatusBadge } from '../../components/Common';
import { formatRelative } from '../../app/format';
import { useLoader } from '../../app/hooks';
import { useSession } from '../../app/session';

interface ShopifyStoreInfo {
  shopDomain: string;
  status: 'installed' | 'uninstalled';
  storeStatus: string;
  installUrl: string;
  needsReauth: boolean;
  scopes: string[];
  installedAt: string;
  uninstalledAt: string | null;
  lastSyncAt: string | null;
  runs: {
    id: string;
    resource: string;
    status: 'running' | 'completed' | 'failed';
    initial: boolean;
    events: number;
    skipped: number;
    error: string | null;
    startedAt: string;
    finishedAt: string | null;
  }[];
}

const RESOURCE_LABELS: Record<string, string> = {
  products: 'Products',
  customers: 'Customers',
  orders: 'Orders',
  abandoned_checkouts: 'Abandoned checkouts',
};

/** Connection details and sync history for a store installed through the Shopify app. */
export function ShopifyPanel({ storeId }: { storeId: string }) {
  const toast = useToast();
  const { can } = useSession();
  const { data, error, loading, reload } = useLoader(
    () => api.get<ShopifyStoreInfo>(`/shopify/stores/${storeId}`),
    storeId,
  );

  const reconnect = async () => {
    try {
      await api.post(`/shopify/stores/${storeId}/reconnect`);
      toast.success('Store reconnected');
      void reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const resync = async () => {
    try {
      await api.post(`/shopify/stores/${storeId}/resync`);
      toast.success('Sync started');
      setTimeout(() => void reload(), 1500);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  if (loading && !data) return <Spinner size="lg" label="Loading" />;
  if (error || !data) return <Alert tone="critical">{error ?? 'Failed to load'}</Alert>;

  return (
    <div className="stack-lg">
      {data.status === 'uninstalled' && (
        <Alert tone="warning" title="App uninstalled">
          The Cartly360 app was removed from this Shopify store{' '}
          {data.uninstalledAt && formatRelative(data.uninstalledAt)}. Your data is kept. Reinstall
          the app to resume syncing.
          <p>
            <a
              className="btn btn--primary btn--sm"
              href={data.installUrl}
              target="_blank"
              rel="noreferrer"
            >
              Reinstall the app
            </a>
          </p>
        </Alert>
      )}
      {data.status === 'installed' && data.storeStatus === 'disconnected' && (
        <Alert tone="warning" title="Store disconnected">
          Syncing is paused and incoming Shopify events are ignored. Your data is kept.
          {can('stores:manage') && (
            <p>
              <Button variant="primary" size="sm" onClick={() => void reconnect()}>
                Reconnect store
              </Button>
            </p>
          )}
        </Alert>
      )}
      {data.needsReauth && data.status === 'installed' && (
        <Alert tone="warning" title="Reconnection needed">
          Shopify access expired. Open the Cartly360 app in your Shopify admin once to reconnect.
        </Alert>
      )}
      <Card
        title="Shopify connection"
        subtitle="This store was connected by installing the Cartly360 app from Shopify."
        actions={
          can('stores:manage') &&
          data.status === 'installed' && (
            <Button icon={<RefreshCw size={14} />} onClick={() => void resync()}>
              Sync now
            </Button>
          )
        }
      >
        <dl className="dl">
          <dt>Shop</dt>
          <dd className="mono">{data.shopDomain}</dd>
          <dt>Status</dt>
          <dd>
            <Badge tone={data.status === 'installed' ? 'success' : 'warning'}>{data.status}</Badge>
          </dd>
          <dt>Last import</dt>
          <dd>{data.lastSyncAt ? formatRelative(data.lastSyncAt) : 'Not yet'}</dd>
          <dt>Permissions</dt>
          <dd className="row">
            {data.scopes.length === 0 ? (
              <span className="text-muted">None recorded</span>
            ) : (
              data.scopes.map((s) => (
                <span key={s} className="badge mono">
                  {s}
                </span>
              ))
            )}
          </dd>
        </dl>
      </Card>
      <Card title="Import history" flush>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Resource</th>
                <th>Type</th>
                <th>Status</th>
                <th className="table__actions">Records</th>
                <th>Started</th>
              </tr>
            </thead>
            <tbody>
              {data.runs.map((r) => (
                <tr key={r.id}>
                  <td className="table__primary">{RESOURCE_LABELS[r.resource] ?? r.resource}</td>
                  <td className="text-sm">{r.initial ? 'Initial import' : 'Refresh'}</td>
                  <td>
                    <StatusBadge
                      status={r.status === 'running' ? 'pending' : r.status}
                      label={r.status}
                    />
                    {r.error && <div className="table__secondary">{r.error}</div>}
                  </td>
                  <td className="table__actions num">
                    {r.events.toLocaleString()}
                    {r.skipped > 0 && <span className="text-muted"> ({r.skipped} skipped)</span>}
                  </td>
                  <td className="text-muted text-sm">{formatRelative(r.startedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
